import { Badge } from "@package/subframe/components/Badge";

import type { EmployeeProfileResponseDto } from "@/generated/model/employeeProfileResponseDto";
import {
  employmentBadgeLabel,
  employmentBadgeVariant,
  formatProfileDate,
  formatProfileDay,
  fullName,
  jobAndRoomLine,
  textOrNotRecorded,
} from "@/lib/people";

import { EmployeeAvatar } from "../EmployeeAvatar";
import { EmployeePhotoMenu } from "../EmployeePhotoMenu";
import { ProfileAcknowledgementsSummary } from "./ProfileAcknowledgementsSummary";

interface ProfileBannerProps {
  profile: EmployeeProfileResponseDto;
  isOwnProfile: boolean;
  onOpenAcknowledgements: () => void;
}

/** Masked like the section cards: the name alone is personal data. */
export function ProfileBanner({
  profile,
  isOwnProfile,
  onOpenAcknowledgements,
}: ProfileBannerProps) {
  const { keyInformation } = profile;
  const name =
    fullName(keyInformation.firstName, keyInformation.lastName) ||
    keyInformation.email;

  return (
    <div
      data-testid="profile-header"
      className="ph-no-capture sentry-block flex w-full flex-col gap-6 rounded-md border border-solid border-neutral-200 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between"
    >
      <div className="flex min-w-0 items-center gap-4 md:gap-6">
        <div className="relative shrink-0">
          <EmployeeAvatar
            name={name}
            photoUrl={keyInformation.photoUrl}
            testId="profile-avatar"
            size="xlarge"
          />
          {profile.capabilities.canEdit && (
            <div className="absolute -bottom-1 -right-1">
              <EmployeePhotoMenu
                nurseryId={profile.nurseryId}
                userId={profile.userId}
                name={name}
                hasPhoto={keyInformation.photoUrl !== null}
              />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-col items-start gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-heading-2 font-heading-2 text-default-font break-words">
              {name}
            </h1>
            <Badge
              variant={employmentBadgeVariant(keyInformation.employmentStatus)}
              data-testid="profile-employment-badge"
            >
              {employmentBadgeLabel(keyInformation.employmentStatus)}
            </Badge>
          </div>
          {keyInformation.preferredName && (
            <span className="text-body text-subtext-color">
              Prefers {keyInformation.preferredName}
            </span>
          )}
          <span className="text-body-bold font-body-bold text-subtext-color break-words">
            {jobAndRoomLine(keyInformation.jobTitle, keyInformation.room?.name)}
          </span>
          <dl className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-x-6">
            <BannerFact
              label="Start date"
              value={formatProfileDate(keyInformation.startDate)}
            />
            {keyInformation.endDate && (
              <BannerFact
                label="End date"
                value={formatProfileDate(keyInformation.endDate)}
                testId="profile-banner-end-date"
              />
            )}
            <BannerFact
              label="Line manager"
              value={textOrNotRecorded(keyInformation.lineManager?.name)}
            />
            <BannerFact
              label="Last updated"
              value={formatProfileDay(profile.lastUpdatedAt)}
              testId="profile-banner-last-updated"
            />
          </dl>
        </div>
      </div>
      <ProfileAcknowledgementsSummary
        className="hidden md:flex"
        summary={profile.acknowledgements}
        name={keyInformation.preferredName || keyInformation.firstName || name}
        isOwnProfile={isOwnProfile}
        onOpen={onOpenAcknowledgements}
      />
    </div>
  );
}

function BannerFact({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="flex gap-1">
      <dt className="text-caption font-caption text-subtext-color">{label}</dt>
      <dd className="text-caption-bold font-caption-bold text-default-font">
        {value}
      </dd>
    </div>
  );
}
